import Config from "@/config";
import {
  AfterRequestInterceptor,
  BeforeRequestInterceptor,
  NetworkProvider,
} from "./network-provider";
import {getToken} from "./firebase";

type RequestOptions = {
  headers: Headers;
  method: string;
  body?: string;
  mode?: RequestMode;
  cache?: RequestCache;
  referrer?: ReferrerPolicy;
  signal?: AbortSignal;
};

// keep track of requests that have already tried to refresh the token
const hasRefreshedToken = {} as Record<string, boolean>;

// append the access token to the request url for RTDB API
const appendRTDBAccessToken: BeforeRequestInterceptor = async (
  request,
  requestOptions,
) => {
  let newRequest: Request | undefined;

  try {
    const url = new URL(request.url);

    // If this request is a retry after 401/403, force token refresh
    // to avoid using a stale cached token
    const isRetryAfterUnauth = hasRefreshedToken[url.origin + url.pathname];
    const token = await getToken(isRetryAfterUnauth);

    if (token) {
      // append the token to request url as a query parameter for Firebase RTDB
      // https://firebase.google.com/docs/database/rest/auth#firebase_id_tokens
      url.searchParams.append("auth", token);
    }

    const options = {
      headers: request.headers,
      method: request.method,
      // The request is rebuilt below; without this a caller's abort signal
      // would stop at the original and never cancel what is actually sent.
      signal: request.signal,
    } as RequestOptions;

    if (request.method !== "GET") {
      options.body = JSON.stringify(requestOptions.json);

      // use Firebase's method override to avoid the preflight request
      // which could make it a little more reliable on flaky networks
      // https://firebase.google.com/docs/reference/rest/database/#section-method-override
      // https://stackoverflow.com/questions/29954037/why-is-an-options-request-sent-and-can-i-disable-it
      // default options are marked with *
      options.method = "POST"; // *GET, POST, PUT, DELETE, etc.
      options.mode = "cors"; // no-cors, cors, *same-origin
      options.cache = "no-cache"; // *default, no-cache, reload, force-cache, only-if-cached
      options.referrer = "no-referrer"; // no-referrer, *client

      // set the content type to text/plain to avoid preflight request
      options.headers?.set("content-type", "text/plain;charset=UTF-8");

      // append the method override
      url.searchParams.append("x-http-method-override", request.method);
    }

    newRequest = new Request(url, options);
  } catch (error) {
    console.error(error);
  }

  return newRequest || request;
};

// append the access token to the request headers for Trendaro API
const appendTrendaroAccessToken: BeforeRequestInterceptor = async (request) => {
  try {
    const token = await getToken();

    if (token) {
      request.headers.append("Authorization", `Bearer ${token}`);
    }
  } catch (error) {
    console.error(error);
  }

  return request;
};

// handle unauthorized requests by refreshing the token
const handleUnauthorized: AfterRequestInterceptor = async (
  request,
  options,
  response,
  context,
) => {
  const isUnauthorized = response.status === 403 || response.status === 401;

  // if the request was successful, return the response
  if (!isUnauthorized) return response;

  // remove the auth token from the URL in a robust way (preserve other query params)
  let url: string;
  let cacheKey: string;
  try {
    const parsed = new URL(request.url);
    // remove only the auth param, keep other params (e.g. x-http-method-override)
    parsed.searchParams.delete("auth");
    url = parsed.toString();
    // use origin+pathname as cache key (ignore query params for tracking)
    cacheKey = parsed.origin + parsed.pathname;
  } catch {
    // fallback to original behaviour if URL parsing fails
    url = request.url.split("?auth")[0];
    cacheKey = url;
  }
  // if we've already tried to refresh the token, return the response
  if (hasRefreshedToken[cacheKey]) {
    hasRefreshedToken[cacheKey] = false;
    return response;
  }

  // Mark this request for token refresh; the beforeRequest interceptor
  // will see this flag and force-refresh the token on retry
  hasRefreshedToken[cacheKey] = true;

  // repeat request with fresh access token (appendRTDBAccessToken will force refresh)
  return await context.client.makeRequest(url, {
    ...options,
    // base URL is included in the request.url
    baseUrl: "",
  });
};

const api = (
  service: "RTDB_API_URL" | "TRENDARO_API_URL" | "CDN_URL",
  hasToken = true,
) => {
  if (hasToken) {
    if (service === "RTDB_API_URL") {
      return new NetworkProvider({
        baseUrl: Config[service],
        interceptors: {
          beforeRequest: [appendRTDBAccessToken],
          afterRequest: [handleUnauthorized],
        },
      });
    } else {
      return new NetworkProvider({
        baseUrl: Config[service],
        interceptors: {
          beforeRequest: [appendTrendaroAccessToken],
          afterRequest: [handleUnauthorized],
        },
      });
    }
  } else {
    return new NetworkProvider({
      baseUrl: Config[service],
    });
  }
};

export {api};
